import { CompanyMention } from "@/components/ai-elements/company-mention";
import { MarkdownTableListActions } from "@/components/ai-elements/markdown-table-list-actions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "@/hooks/useTranslation";
import type { CompanyListState } from "@/lib/company-list-parts";
import { sourceLink } from "@/lib/source-link";
import type { CompanyMentionCollection } from "@/lib/company-mention-nodes";
import { hasReliableCompanyIdentifier } from "@/lib/company-mention-reliability";
import type { CompanyResolutionParams } from "@/lib/company-resolution";
import { toCompanyTag } from "@/lib/company-tag";
import type { WebSearchToolCompany } from "@shaarp/common";
import { Loader2Icon } from "lucide-react";
import { useMemo, useRef } from "react";

const COLUMN_COUNT = 5;

function CompanyRow({ row }: { row: WebSearchToolCompany }) {
  const link = sourceLink(row.sourceUrl);

  return (
    <TableRow>
      <TableCell className="font-medium">
        <CompanyMention
          name={row.name}
          domain={row.domain}
          city={row.city}
          activity={row.specialty}
          source={row.sourceUrl}
        >
          {row.name}
        </CompanyMention>
      </TableCell>
      <TableCell>{row.city ?? "—"}</TableCell>
      <TableCell>{row.specialty ?? "—"}</TableCell>
      <TableCell>{row.estimate ?? "—"}</TableCell>
      <TableCell>
        {link ? (
          <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground underline-offset-2 hover:underline"
          >
            {link.host}
          </a>
        ) : (
          "—"
        )}
      </TableCell>
    </TableRow>
  );
}

const rowKey = (row: WebSearchToolCompany): string =>
  `${row.name}|${row.city ?? ""}|${row.sourceUrl}`;

/**
 * The rows an identifier resolves, and the others — saved from the tag their
 * chip carries: the same split as a table in the text.
 */
function splitRowsToSave(
  rows: readonly WebSearchToolCompany[],
): CompanyMentionCollection {
  const collection: CompanyMentionCollection = {
    mentions: [],
    unidentified: [],
  };
  for (const row of rows) {
    const params: CompanyResolutionParams = {
      name: row.name,
      ...(row.domain && { domain: row.domain }),
      ...(row.city && { city: row.city }),
    };
    if (hasReliableCompanyIdentifier(params)) {
      collection.mentions.push(params);
      continue;
    }
    const tag = toCompanyTag({
      ...params,
      activity: row.specialty,
      source: row.sourceUrl,
    });
    if (tag) collection.unidentified.push(tag);
  }
  return collection;
}

/**
 * The list the model builds with addCompanies, filling as it reads: exact
 * matches first, then the close ones in a group of their own. The model's
 * answer only introduces it — the rows live here, once.
 *
 * Running for as long as the answer is live, not only while an addCompanies
 * call is: between two calls the model is still searching, and a list offered
 * for saving then would be saved half-built.
 */
export function ChatCompanyListCard({
  list,
  isLive,
}: {
  list: CompanyListState;
  isLive: boolean;
}) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLDivElement>(null);
  const exact = list.rows.filter((row) => !row.isCloseMatch);
  const close = list.rows.filter((row) => row.isCloseMatch);
  const hasEstimates = list.rows.some((row) => row.estimate?.includes("≈"));

  // The target arrives with the first addCompanies call: until then the
  // counter stands alone rather than reading "0/0". While the model writes a
  // call, its companies count in already — the server takes them only once
  // the whole call is written, tens of seconds during which the counter would
  // otherwise sit still, and the wait is adding, not searching.
  const writingCount = list.writingCount ?? 0;
  // Rows past the target are refused by the server: never read "57/50".
  const liveCount = Math.min(
    list.total + writingCount,
    list.targetCount ?? Number.POSITIVE_INFINITY
  );
  const header = !isLive
    ? t("chat.message.companyList.count", { count: list.total })
    : writingCount > 0
      ? list.targetCount === undefined
        ? t("chat.message.companyList.addingNoTarget", { count: liveCount })
        : t("chat.message.companyList.adding", {
            count: liveCount,
            target: list.targetCount,
          })
      : list.targetCount === undefined
        ? t("chat.message.companyList.runningNoTarget", { count: list.total })
        : t("chat.message.companyList.running", {
            count: list.total,
            target: list.targetCount,
          });

  const companies = useMemo(() => splitRowsToSave(list.rows), [list.rows]);

  return (
    <div className="my-4 space-y-2">
      <span className="flex items-center gap-2 text-sm font-medium">
        {isLive && <Loader2Icon className="size-4 animate-spin" />}
        {header}
      </span>
      {list.rows.length > 0 && (
        <div
          ref={tableRef}
          className="overflow-hidden rounded-xl border bg-background"
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("chat.message.companyList.columns.company")}</TableHead>
                <TableHead>{t("chat.message.companyList.columns.city")}</TableHead>
                <TableHead>{t("chat.message.companyList.columns.specialty")}</TableHead>
                <TableHead>{t("chat.message.companyList.columns.estimate")}</TableHead>
                <TableHead>{t("chat.message.companyList.columns.source")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exact.map((row) => (
                <CompanyRow key={rowKey(row)} row={row} />
              ))}
              {close.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className="bg-muted/50 text-xs font-medium text-muted-foreground"
                  >
                    {t("chat.message.companyList.closeGroup")}
                  </TableCell>
                </TableRow>
              )}
              {close.map((row) => (
                <CompanyRow key={rowKey(row)} row={row} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {hasEstimates && (
        <p className="text-xs text-muted-foreground">
          {t("chat.message.companyList.estimateLegend")}
        </p>
      )}
      {!isLive && (
        <MarkdownTableListActions companies={companies} tableRef={tableRef} />
      )}
    </div>
  );
}
